import React from 'react';
import { useTranslation } from '../../../common/components/LocalizationProvider';
import LogoImage from '../../../common/auth/LogoImage';
import { formatTime } from '../../../common/util/formatter';

/**
 * PrintHeader - Cabeçalho profissional para relatórios
 *
 * @param {Object} props
 * @param {string} props.title - Título do relatório
 * @param {string} [props.subtitle] - Subtítulo opcional
 * @param {string|Date} [props.from] - Data/hora inicial
 * @param {string|Date} [props.to] - Data/hora final
 * @param {string} [props.deviceName] - Nome do(s) dispositivo(s)
 * @param {string} [props.companyName] - Nome da empresa/revenda
 * @param {Function} [props.onLogoLoad] - Callback quando logo carrega
 * @param {Function} [props.onLogoError] - Callback se logo falhar
 */
const PrintHeader = ({
  title,
  subtitle,
  from,
  to,
  deviceName,
  companyName,
  onLogoLoad,
  onLogoError,
}) => {
  const t = useTranslation();

  // Formatar data/hora
  const formatDateTime = (value) => {
    if (!value) return null;
    try {
      const date = value instanceof Date ? value : new Date(value);
      return formatTime(date, 'minutes');
    } catch {
      return String(value);
    }
  };

  const fromFormatted = formatDateTime(from);
  const toFormatted = formatDateTime(to);

  // Verificar se há metadados para mostrar
  const hasMetadata = deviceName || fromFormatted || toFormatted;

  return (
    <header className="print-header">
      <div className="print-header__row">
        {/* Logo */}
        <div className="print-header__logo">
          <LogoImage
            forceLightTheme
            height={50}
            alt={companyName || 'Logo'}
            onLoad={onLogoLoad}
            onError={onLogoError}
          />
        </div>

        {/* Título Central */}
        <div className="print-header__center">
          <h1 className="print-header__title">
            {title || t('reportTitle')}
          </h1>
          {subtitle && (
            <p className="print-header__subtitle">{subtitle}</p>
          )}
        </div>

        {/* Nome da Empresa */}
        <div className="print-header__company">
          {companyName}
        </div>
      </div>

      {/* Metadados */}
      {hasMetadata && (
        <div className="print-header__meta">
          {deviceName && (
            <div className="print-header__meta-item">
              <span className="print-header__meta-label">{t('sharedDevice')}:</span>
              <span className="print-header__meta-value">{deviceName}</span>
            </div>
          )}
          {fromFormatted && (
            <div className="print-header__meta-item">
              <span className="print-header__meta-label">{t('reportFrom')}:</span>
              <span className="print-header__meta-value">{fromFormatted}</span>
            </div>
          )}
          {toFormatted && (
            <div className="print-header__meta-item">
              <span className="print-header__meta-label">{t('reportTo')}:</span>
              <span className="print-header__meta-value">{toFormatted}</span>
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default PrintHeader;
